<template>
  <v-container fluid style="height: 100%">
    <v-card style="height: 100%">
      <v-card-title class="headline ">
        Hostel
        <v-spacer></v-spacer>
        <div>
          <v-select dense light :items="sessions" item-text="name" v-model="session" return-object placeholder="Session"></v-select>
        </div>
      </v-card-title>

      <v-tabs
          v-model="activeTab"
          background-color="primary"
          dark
          grow
      >
        <v-tab key="registration">
          <v-icon left>mdi-home-city</v-icon>
          Hostel Application
        </v-tab>
        <v-tab key="payment-history" :disabled="!studentPrivilege?.eligible">
          <v-icon left>mdi-history</v-icon>
          Payment History
        </v-tab>
        <v-tab key="space" :disabled="!studentPrivilege?.eligible || !hostelAssignment?.approved">
          <v-icon left>mdi-home</v-icon>
          My Space
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" v-if="sessionSelected && studentPrivilege">
        <!-- Hostel Registration Tab -->
        <v-tab-item key="registration">
          <v-card flat>
            <v-card-text>
              <div v-if="studentPrivilege.eligible">
                <v-stepper v-model="currentStep">
                  <!-- Stepper Headers -->
                  <v-stepper-header>
                    <v-stepper-step :complete="currentStep > 1" step="1">
                      Eligibility Check
                    </v-stepper-step>
                    <v-divider></v-divider>

                    <v-stepper-step :complete="currentStep > 2" step="2">
                      Interest Fee
                    </v-stepper-step>
                    <v-divider></v-divider>

                    <v-stepper-step :complete="currentStep > 3" step="3">
                      Hostel Selection
                    </v-stepper-step>
                    <v-divider></v-divider>

                    <v-stepper-step :complete="currentStep > 4" step="4">
                      Hostel Fee
                    </v-stepper-step>
                    <v-divider></v-divider>

                    <v-stepper-step step="5" :complete="currentStep > 5">
                      Maintenance Fee
                    </v-stepper-step>
                  </v-stepper-header>

                  <!-- Stepper Content -->
                  <v-stepper-items>
                    <!-- Step 1: Eligibility -->
                    <v-stepper-content step="1">
                      <v-card class="mb-4" outlined>
                        <v-card-title>Hostel Eligibility Status</v-card-title>
                        <v-card-text>
                          <v-alert type="success" dense text>
                            Hi🆗, you are eligible for hostel accommodation
                          </v-alert>
                        </v-card-text>
                      </v-card>
                      <v-btn color="primary" @click="currentStep = 2">
                        Continue
                      </v-btn>
                    </v-stepper-content>

                    <!-- Step 2: Interest Fee -->
                    <v-stepper-content step="2" v-if="studentPrivilege?.eligible">
                      <fee-payment-card
                          title="Interest Fee Payment"
                          :amount="studentPrivilege.interestFeeAmount"
                          :is-required="studentPrivilege.requiresInterestFee"
                          :is-paid="interestFeePaid"
                          fee-type="interest"
                          @pay="payInterestFee"
                          @download-invoice="downloadInvoice('interest')"
                          @download-receipt="downloadReceipt('interest')"
                          :payment.sync="studentPrivilege.interestPayment"
                          :payment-platform="studentPrivilege.interestPlatform"
                          :session="session"
                          :payment-enabled="studentPrivilege.canPayInterestFee"
                      />
                      <v-btn
                          color="primary"
                          @click="currentStep = 3"
                          :disabled="studentPrivilege.requiresInterestFee && !interestFeePaid"
                      >
                        Continue
                      </v-btn>
                      <v-btn text @click="currentStep = 1">
                        Back
                      </v-btn>
                    </v-stepper-content>

                    <!-- Step 3: Hostel Assignment -->
                    <v-stepper-content step="3" v-if="studentPrivilege?.eligible">
                      <hostel-assignment-card
                          :assignment.sync="hostelAssignment"
                          :interest="studentPrivilege.interest"
                      />
                      <v-btn
                          color="primary"
                          @click="currentStep = 4"
                          :disabled="!hostelAssignment"
                      >
                        Continue
                      </v-btn>
                      <v-btn text @click="currentStep = 2">
                        Back
                      </v-btn>
                    </v-stepper-content>

                    <!-- Step 4: Hostel Fee -->
                    <v-stepper-content step="4">
                      <fee-payment-card
                          title="Hostel Fee Payment"
                          :amount="studentPrivilege.hostelFee"
                          :is-required="studentPrivilege.requiresHostelFee"
                          :is-paid="hostelFeePaid"
                          fee-type="hostel"
                          @pay="payHostelFee"
                          @download-invoice="downloadInvoice('hostel')"
                          @download-receipt="downloadReceipt('hostel')"
                          :payment="studentPrivilege.hostelPayment"
                          :payment-platform="studentPrivilege.hostelPlatform"
                          :session="session"
                          :payment-enabled="studentPrivilege.canPayHostelFee"
                      />
                      <v-btn
                          color="primary"
                          @click="currentStep = 5"
                          :disabled="(!hostelFeePaid && studentPrivilege.requiresHostelFee) || (hostelAssignment && !hostelAssignment.approved)"
                      >
                        Continue
                      </v-btn>
                      <v-btn text @click="currentStep = 3">
                        Back
                      </v-btn>
                    </v-stepper-content>

                    <!-- Step 5: Maintenance Fee -->
                    <v-stepper-content step="5">
                      <fee-payment-card
                          title="Maintenance Fee"
                          :amount="studentPrivilege.maintenanceFee"
                          :is-required="studentPrivilege.requiresMaintenanceFee"
                          :is-paid="maintenanceFeePaid"
                          fee-type="maintenance"
                          @pay="payMaintenanceFee"
                          @download-invoice="downloadInvoice('maintenance')"
                          @download-receipt="downloadReceipt('maintenance')"
                          :payment="studentPrivilege.maintenancePayment"
                          :payment-platform="studentPrivilege.maintenancePlatform"
                          :session="session"
                          :payment-enabled="studentPrivilege.canPayMaintenanceFee"
                      />
                      <v-btn text @click="currentStep = 4">
                        Back
                      </v-btn>
                    </v-stepper-content>
                  </v-stepper-items>
                </v-stepper>
              </div>
              <v-alert v-else type="error" dense text>
                Sorry, you are not eligible for hostel accommodation
              </v-alert>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- Payment History Tab -->
        <v-tab-item key="payment-history" :disabled="!studentPrivilege?.eligible" v-if="studentPrivilege?.eligible">
          <v-card flat>
            <v-card-title>
              Payment History
              <v-spacer></v-spacer>
            </v-card-title>
            <v-card-text>
              <v-data-table
                  :headers="paymentHistoryHeaders"
                  :items="paymentHistory"
                  :items-per-page="5"
                  class="elevation-1"
              >
                <template v-slot:item.status="{ item }">
                  <v-chip
                      small
                      :color="item.status === 'Paid' ? 'success' : 'warning'"
                      text-color="white"
                  >
                    {{ item.status }}
                  </v-chip>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn
                      x-small
                      text
                      color="primary"
                      @click="downloadInvoice(item.reference, item.type.toLowerCase())"
                  >
                    <v-icon color="primary">mdi-download-multiple</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <v-tab-item key="space">
          <v-card class="room-assignment-card mt-4 mb-4 ma-8" elevation="0">
            <v-card-title :class="{'primary darken-2 white--text': !!hostelAssignment}"><v-icon left color="white">mdi-home-city</v-icon>
              {{'My Space'}}</v-card-title>
            <v-card-text v-if="hostelAssignment" class="">
              <student-space-information :assignment="hostelAssignment" :config="studentPrivilege"></student-space-information>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
      <v-card-text v-else>
        <v-alert type="error" dense text>
          Select session to continue
        </v-alert>
      </v-card-text>
    </v-card>
    <loading-dialog :show="loading"></loading-dialog>
  </v-container>
</template>

<script>
// script.js
import FeePaymentCard from "@/student/components/hostel/FeePaymentCard.vue";
import HostelAssignmentCard from "@/student/components/hostel/HostelAssignmentCard.vue";
import Api from "@/main/api";
import ErrorResolver from "@/shared/ErrorResolver";
import {mapGetters} from "vuex";
import EnhancedRoomSelection from "@/student/components/hostel/RoomSelection.vue";
import LoadingDialog from "@ui/components/LoadingDialog.vue";
import StudentSpaceInformation from "@/student/components/hostel/StudentSpaceInformation.vue";
import {formatCurrency} from "@/shared/CurrencyUtil";

export default {
  name: 'HostelManagement',

  components: {
    StudentSpaceInformation,
    LoadingDialog,
    EnhancedRoomSelection,
    FeePaymentCard,
    HostelAssignmentCard
  },

  data: () => ({
    activeTab: 'registration',
    currentStep: 1,
    interestFeePaid: false,
    hostelFeePaid: false,
    maintenanceFeePaid: false,
    sessions: [],
    session: null,
    loading: false,

    // Sample data - replace with actual API calls
    studentPrivilege: {
      studentId: "2024001",
      semester: "Spring 2024",
      requiresInterestFee: true,
      requiresHostelFee: true,
      interestFeeAmount: 100,
      hostelFee: 1500,
      requiresMaintenanceFee: true,
      maintenanceFee: 200,
      eligible: false,
      canPayHostelFee: false,
      canPayInterestFee: false,
      canPayMaintenanceFee: false,
      interestPayment: null,
      hostelPayment: null,
      maintenancePayment: null,
      interestPlatform: null,
      hostelPlatform: null,
      maintenancePlatform: null,
      allocation: null,
      interest: null
    },

    hostelAssignment: {},

    paymentHistoryHeaders: [
      { text: 'Date', value: 'date' },
      { text: 'Fee Type', value: 'type' },
      { text: 'Amount', value: 'amount' },
      { text: 'Status', value: 'status' },
      { text: 'Reference', value: 'reference' },
      { text: 'Actions', value: 'actions', sortable: false }
    ]
  }),

  computed: {
    ...mapGetters(['getUser']),
    hasAnyPayments() {
      return this.interestFeePaid || this.hostelFeePaid || this.maintenanceFeePaid;
    },

    sessionSelected(){
      return this.session != null
    },

    paymentHistory() {
      const history = [];

      if (this.studentPrivilege.requiresInterestFee && this.studentPrivilege.interestPayment) {
        history.push({
          date: new Date().toLocaleDateString(),
          type: 'Interest',
          amount: formatCurrency(this.studentPrivilege.interestFeeAmount),
          status: this.interestFeePaid ? 'Paid' : 'Pending',
          reference: this.studentPrivilege.interestPayment?.transactionId ??  '-'
        });
      }

      if (this.studentPrivilege.requiresHostelFee && this.studentPrivilege.hostelPayment) {
        history.push({
          date: new Date().toLocaleDateString(),
          type: 'Hostel',
          amount: formatCurrency(this.studentPrivilege.hostelFee),
          status: this.hostelFeePaid ? 'Paid' : 'Pending',
          reference: this.studentPrivilege.hostelPayment?.transactionId ??  '-'
        });
      }

      if (this.studentPrivilege.requiresMaintenanceFee && this.studentPrivilege.maintenancePayment) {
        history.push({
          date: new Date().toLocaleDateString(),
          type: 'Maintenance',
          amount: formatCurrency(this.studentPrivilege.maintenanceFee),
          status: this.maintenanceFeePaid ? 'Paid' : 'Pending',
          reference: this.studentPrivilege.maintenancePayment?.transactionId ??  '-'
        });
      }

      return history;
    }
  },

  methods: {
    async getStudentHostelInformation(){
      if(!this.session)
        return
      this.loading = true
      Api.hostel.getStudentInformation(this.getUser.userId, this.session.id)
          .then(res => {
            this.studentPrivilege = res.data
            this.interestFeePaid = this.studentPrivilege?.interestPayment?.cleared || false
            this.hostelFeePaid = this.studentPrivilege?.hostelPayment?.cleared || false
            this.maintenanceFeePaid = this.studentPrivilege?.maintenancePayment?.cleared || false
            this.hostelAssignment = this.studentPrivilege?.allocation
            this.studentPrivilege.canPayMaintenanceFee = this.studentPrivilege.canPayMaintenanceFee && this.hostelAssignment?.approved
            // console.log(res.data)
          })
          .catch(err => ErrorResolver.process(this, err))
          .finally(() => this.loading = false)
    },

    async payInterestFee() {
      try {
        // Implement payment gateway integration here
        await this.processPayment('interest', this.studentPrivilege.interestFeeAmount);
        this.interestFeePaid = true;
        this.$emit('payment-completed', {
          type: 'interest',
          amount: this.studentPrivilege.interestFeeAmount
        });
        this.$notify({ type: 'success', message: 'Interest fee payment successful' });
      } catch (error) {
        this.$notify({ type: 'error', message: 'Payment failed. Please try again.' });
      }
    },

    async payHostelFee() {
      try {
        await this.processPayment('hostel', this.studentPrivilege.hostelFee);
        this.hostelFeePaid = true;
        this.$emit('payment-completed', {
          type: 'hostel',
          amount: this.studentPrivilege.hostelFee
        });
        this.$notify({ type: 'success', message: 'Hostel fee payment successful' });
      } catch (error) {
        this.$notify({ type: 'error', message: 'Payment failed. Please try again.' });
      }
    },

    async payMaintenanceFee() {
      try {
        await this.processPayment('maintenance', this.studentPrivilege.maintenanceFee);
        this.maintenanceFeePaid = true;
        this.$emit('payment-completed', {
          type: 'maintenance',
          amount: this.studentPrivilege.maintenanceFee
        });
        this.$notify({ type: 'success', message: 'Maintenance fee payment successful' });
      } catch (error) {
        this.$notify({ type: 'error', message: 'Payment failed. Please try again.' });
      }
    },

    async processPayment(type, amount) {
      // Implement payment gateway integration here
      return new Promise((resolve) => {
        setTimeout(resolve, 1000); // Simulating payment processing
      });
    },

    // invoice download
    downloadInvoice(invoice, type) {
      this.loading = true;

      Api.hostel.invoice(invoice)
          .then(res => {
            let fileURL = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            let fileLink = document.createElement('a');

            fileLink.href = fileURL;
            fileLink.setAttribute('download', type+ '-invoice.pdf');
            document.body.appendChild(fileLink);

            fileLink.click();
            fileLink.remove();
          }).catch(() => {
        // ErrorReslover.checkErrors(this, err);
        this.$notify({ type: 'error', message: 'Failed to download invoice' });
        // err.message = "Failed to Download Invoice"
      }).finally(() => this.loading = false)
    },

    downloadReceipt(type) {
      const receiptData = {
        interest: {
          amount: this.studentPrivilege.interestFeeAmount,
          type: 'Interest Fee',
          paid: this.interestFeePaid
        },
        hostel: {
          amount: this.studentPrivilege.hostelFee,
          type: 'Hostel Fee',
          paid: this.hostelFeePaid
        },
        maintenance: {
          amount: this.studentPrivilege.maintenanceFee,
          type: 'Maintenance Fee',
          paid: this.maintenanceFeePaid
        }
      };

      if (receiptData[type].paid) {
        // Implement receipt generation and download
        console.log(`Downloading ${type} receipt:`, receiptData[type]);
      }
    },

    downloadAllReceipts() {
      const paidFees = [];
      if (this.interestFeePaid) paidFees.push('interest');
      if (this.hostelFeePaid) paidFees.push('hostel');
      if (this.maintenanceFeePaid) paidFees.push('maintenance');

      paidFees.forEach(this.downloadReceipt);
    },

    validateStep(step) {
      switch (step) {
        case 1:
          return true;
        case 2:
          return !this.studentPrivilege.requiresInterestFee || this.interestFeePaid;
        case 3:
          return !!this.hostelAssignment;
        case 4:
          return this.hostelFeePaid || !this.studentPrivilege.requiresHostelFee;
        case 5:
          return !this.studentPrivilege.requiresMaintenanceFee || this.hostelFeePaid || !this.studentPrivilege.requiresHostelFee;
        default:
          return true;
      }
    },

    loadSessions() {
      this.loading = true
      Api.session.getSessionsForPayment(this.getUser.userId)
          .then(res => this.sessions = res.data)
          .catch(err => ErrorResolver.checkErrors(this, err))
          .finally(() => this.loading = false);
    },
  },

  watch: {
    currentStep(newStep) {
      if (!this.validateStep(newStep - 1)) {
        this.currentStep = newStep - 1;
      }
    },
    getUser: function (val) {
      if (val)
        this.loadSessions()
    },

    session: function(){
      this.studentPrivilege = null
      this.getStudentHostelInformation()
    }
  },

  created() {
    if (this.getUser != null)
      this.loadSessions()
  }
};
</script>

<style scoped>
.v-stepper {
  box-shadow: none !important;
}

.v-card {
  transition: all 0.3s ease-in-out;
}

.v-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

.v-btn {
  text-transform: none;
}
</style>
